<template>
  <div :class="[$style.container, $style.flexColumn]">
    <div :class="$style.graphicContainer">
      <transition
        :enterActiveClass="$style.fadeEnterActive"
        :enterClass="$style.enterClass"
      >
        <div
          v-if="renderAnimationElement"
          ref="emptyStateAnimationDiv"
        />
      </transition>
    </div>
    <div :class="[$style.textsContainer, $style.flexColumn]">
      <HeadingLarge>
        <span :class="$style.addApplicationHeadingTextColor">
          Add your first application
        </span>
      </HeadingLarge>

      <HeadingSmall>
        Single Sign-On and Identity Management Application Integrations
      </HeadingSmall>

      <ParagraphBody bodySize="large">
        Set up and manage single sign-on or create an identity management
        integration to import, update, and export users on a regular basis.
      </ParagraphBody>
    </div>

    <Button
      ariaLabel="add first application button"
      :class="$style.getStartedButton"
      :data-test-id="$testId('GetStartedButton')"
      text="get started"
      textCase="capitalize"
      type="primary"
      @click="handleGetStartedButtonClick"
    />

    <Card
      :class="$style.proTipCard"
      type="informational"
    >
      <div :class="$style.cardContentsContainer">
        <Icon
          :class="$style.infoIcon"
          icon="info"
          :scale="1.25"
        />
        <ParagraphBody :hasMargin="false">
          <template v-slot:content>
            <div
              :class="$style.cardText"
            >
              <strong>Pro Tip: </strong>
              You can connect nearly any HR Directory or Identity Provider
              to easily import new users into JumpCloud!
            </div>
          </template>
        </ParagraphBody>
      </div>
    </Card>
  </div>
</template>

<script>
import {
  Button,
  Card,
  HeadingLarge,
  HeadingSmall,
  Icon,
  ParagraphBody,
} from '@jumpcloud/ui-components';

import Util from 'client/util/Util';

export default {
  name: 'ApplicationItemListEmptyState',

  components: {
    Button,
    Card,
    HeadingLarge,
    HeadingSmall,
    Icon,
    ParagraphBody,
  },

  data: () => ({
    renderAnimationElement: false,
  }),

  mounted() {
    this.handleLottieAnimationLoad();
  },

  methods: {
    handleGetStartedButtonClick() {
      const route = this.$hasFeatureFlag(this.$featureFlags.applicationWorkflow)
        ? '#/applications/create'
        : '#/sso/choose';

      // @todo: here to call out replacing this with a vue router method
      Util.redirect(route);
    },

    async handleLottieAnimationLoad() {
      /**
       * Zach Smith, HY-2300
       * Previously we were using the "light" version of the lottie-web module,
       * at `lottie-web/build/player/lottie_light.min`, however we began to see weird color
       * and animation inconsistencies between what the animation should look like (while
       * previewing on the Lottie website) and when using that animation within AP.
       *
       * We discovered that it was due to the usage of the "light" version of the module, and
       * switching to the "full" version fixed the inconsistencies. Although the response size
       * is bigger when fetching the full module, it's still being lazy-loaded, so our FE bundle
       * size remains unaffected.
       *
       * We are lazy-loading the Lottie module and the Lottie JSON animation file here as we didn't
       * want to eager-load them in the bundle and balloon the bundle size.
       *
       * Relevant slack threads:
       * FE bundle size thread: https://thejumpcloud.slack.com/archives/C035ZE4CWHH/p1670262141207389
       * Lazy-loading solution: https://thejumpcloud.slack.com/archives/C035ZE4CWHH/p1670356189747649
       */
      const [lottieWeb, animationFileModule] = await Promise.all([
        import('lottie-web'),
        import('../../../public/img/lottieJsons/ApplicationMainPanelEmptyStateWithPersonio.json'),
      ]);

      const animationFile = animationFileModule.default;
      this.renderAnimationElement = true;

      // here, we look for the prefers-reduced-motion setting on the browser
      // if it's set to anything other than 'no-preference' (hence, the 'reduce' setting)
      // then we disable autoplay, so it just displays a still image of the first frame
      const reducedMotionQueryString = '(prefers-reduced-motion: no-preference)';
      const shouldAutoplayAnimation = window.matchMedia(reducedMotionQueryString).matches;

      // wait a tick for the animation wrapper element to render,
      // and to apply a slight fade-in transition
      this.$nextTick(() => {
        lottieWeb.loadAnimation({
          container: this.$refs.emptyStateAnimationDiv,
          renderer: 'svg',
          loop: true,
          autoplay: shouldAutoplayAnimation,
          animationData: animationFile,
        });
      });
    },
  },
};
</script>

<style scoped module>
@value (fadeTransition) from '~css/transitions.css';

.container {
  align-items: center;
  display: flex;
  flex-direction: column;
  margin: auto;
  text-align: center;
  width: 600px;
}

.addApplicationHeadingTextColor {
  color: var(--jc-color-neutral-text-subdued);
}

.cardContentsContainer {
  align-items: center;
  column-gap: var(--jc-spacer-medium);
  display: flex;
}

.cardText {
  text-align: left;
}

.getStartedButton {
  width: 125px;
}

.getStartedButton,
.textsContainer {
  margin-bottom: var(--jc-spacer-large);
}

.graphicContainer {
  aspect-ratio: 1.6;
  height: 500px;
  width: auto;
}

.infoIcon {
  color: var(--jc-color-informational-base);
}

.proTipCard {
  width: 480px;
}

/* Utility classes */

.flexColumn {
  display: flex;
  flex-direction: column;
}

.enterClass {
  opacity: 0;
}

/* Transitions */
.fadeEnterActive {
  transition: fadeTransition;
}
</style>
